/**
 * V3 — Storage Health admin dashboard.
 * Liste tous les devices de tous les utilisateurs avec stats localStorage temps réel.
 * Indique : taille stockée, équivalent décompressé, ratio quota, top clés.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { api } from './api';
import { HardDrive, RefreshCw, AlertTriangle, CheckCircle2, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

const fmtKb = (b) => (b / 1024).toFixed(0);
const fmtMb = (b) => (b / 1024 / 1024).toFixed(2);
const fmtAge = (min) => {
  if (min < 1) return 'à l\'instant';
  if (min < 60) return `il y a ${min} min`;
  if (min < 60 * 24) return `il y a ${Math.round(min / 60)}h`;
  return `il y a ${Math.round(min / 60 / 24)}j`;
};

function quotaColor(ratio) {
  if (ratio >= 0.90) return { bg: '#7f1d1d', fg: '#fee2e2', label: 'CRITIQUE' };
  if (ratio >= 0.70) return { bg: '#d97706', fg: '#fef3c7', label: 'ATTENTION' };
  return { bg: '#15803d', fg: '#dcfce7', label: 'OK' };
}

export default function V3StorageHealth() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const { data: d } = await api.get('/api/legacy/health/storage-overview');
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 60000); // auto-refresh chaque minute
    return () => clearInterval(iv);
  }, [fetchData]);

  const handleDelete = async (username, device_id) => {
    if (!confirm(`Supprimer le rapport de ${username} / ${device_id.slice(0, 12)}... ?`)) return;
    try {
      await api.delete(`/api/legacy/health/storage-report/${encodeURIComponent(username)}/${encodeURIComponent(device_id)}`);
      fetchData();
    } catch (e) {
      alert('Erreur suppression : ' + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="v3-page" data-testid="v3-storage-health">
      <div className="v3-page-head">
        <h1><HardDrive size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Santé du stockage par appareil
        </h1>
        <p>Vue admin : chaque appareil connecté reporte ses stats localStorage toutes les 5 min. Tu peux anticiper les soucis quota avant qu&apos;un employé ne soit bloqué.</p>
      </div>

      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
        <button onClick={fetchData} disabled={loading} data-testid="refresh-btn"
                style={{ padding: '8px 16px', background: '#1e3a8a', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          {loading ? 'Chargement…' : 'Actualiser'}
        </button>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>Auto-refresh : 60 secondes</span>
      </div>

      {err && (
        <div style={{ padding: 14, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#fca5a5', marginBottom: 16 }}>
          <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          {err}
        </div>
      )}

      {data && data.summary && (
        <div className="v3-kpi-grid" data-testid="summary-kpis"
             style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
          <KpiCard label="Appareils" value={data.summary.total_devices} icon={<HardDrive size={18} />} color="#1e3a8a" />
          <KpiCard label="Utilisateurs" value={data.summary.unique_users} color="#0369a1" />
          <KpiCard label="Critiques (≥90%)" value={data.summary.critical_count} color="#7f1d1d" />
          <KpiCard label="Attention (70-90%)" value={data.summary.warning_count} color="#d97706" />
          <KpiCard label="OK (<70%)" value={data.summary.ok_count} color="#15803d" />
          <KpiCard label="Obsolètes (>1h)" value={data.summary.stale_count} color="#475569" />
          <KpiCard label="Stockage total" value={data.summary.total_mb_stored + ' MB'}
                   sub={`vs ${data.summary.total_mb_decompressed} MB décompressé`}
                   color="#84cc16" />
          <KpiCard label="Ratio compression" value={data.summary.global_compression_pct + '%'}
                   sub={`gain ${100 - data.summary.global_compression_pct}%`}
                   color="#0891b2" />
        </div>
      )}

      {data && data.items && data.items.length === 0 && (
        <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', background: '#0f172a', borderRadius: 8 }}>
          <HardDrive size={36} style={{ opacity: 0.4, marginBottom: 12 }} />
          <div style={{ fontSize: 16, fontWeight: 600 }}>Aucun rapport reçu pour l&apos;instant</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>
            Les rapports arrivent automatiquement quand un utilisateur se connecte sur l&apos;app legacy.
            Si tu en attends mais qu&apos;ils n&apos;arrivent pas, vérifie que ton équipe a bien la dernière version (redéploiement effectué).
          </div>
        </div>
      )}

      {data && data.items && data.items.length > 0 && (
        <div style={{ background: '#0f172a', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="health-table">
            <thead style={{ background: '#1e293b' }}>
              <tr>
                <th style={th}>Utilisateur</th>
                <th style={th}>Appareil</th>
                <th style={th}>Statut</th>
                <th style={th}>Stocké</th>
                <th style={th}>Décompressé</th>
                <th style={th}>Ratio</th>
                <th style={th}>Clés (comp / total)</th>
                <th style={th}>Dernier rapport</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => {
                const c = quotaColor(it.quota_ratio);
                const key = `${it.username}__${it.device_id}`;
                const isOpen = !!expanded[key];
                return (
                  <React.Fragment key={key}>
                    <tr style={{ borderTop: '1px solid #1e293b', opacity: it.stale ? 0.5 : 1 }}>
                      <td style={td}><strong style={{ color: '#fff' }}>{it.username}</strong></td>
                      <td style={td} title={it.device_id}>
                        <code style={{ fontSize: 11, color: '#94a3b8' }}>{it.device_id.slice(0, 12)}…</code>
                        {it.user_agent && (
                          <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
                            {it.user_agent.slice(0, 60)}{it.user_agent.length > 60 ? '…' : ''}
                          </div>
                        )}
                      </td>
                      <td style={td}>
                        <span style={{ background: c.bg, color: c.fg, padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
                          {c.label}
                        </span>
                        {it.quota_purged_recently && (
                          <span style={{ marginLeft: 6, background: '#7f1d1d', color: '#fee2e2', padding: '3px 6px', borderRadius: 4, fontSize: 10 }}>
                            Purge récente
                          </span>
                        )}
                      </td>
                      <td style={td}>{fmtKb(it.bytes_in_storage)} KB</td>
                      <td style={td}>{fmtMb(it.bytes_decompressed)} MB</td>
                      <td style={td}>
                        <strong style={{ color: c.bg }}>{(it.quota_ratio * 100).toFixed(1)}%</strong>
                        <div style={{ fontSize: 10, color: '#64748b' }}>compression {it.compression_ratio_pct}%</div>
                      </td>
                      <td style={td}>
                        <span title="Clés compressées">{it.compressed_keys}</span> / <span title="Total clés">{it.total_keys}</span>
                      </td>
                      <td style={td}>{fmtAge(it.age_minutes)}</td>
                      <td style={td}>
                        <button onClick={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))}
                                data-testid={`expand-${it.username}`}
                                style={iconBtn} title="Voir les top clés">
                          {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                        <button onClick={() => handleDelete(it.username, it.device_id)}
                                data-testid={`delete-${it.username}`}
                                style={{ ...iconBtn, color: '#ef4444' }} title="Supprimer ce rapport (device obsolète)">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                    {isOpen && it.top_keys && it.top_keys.length > 0 && (
                      <tr style={{ background: '#020617' }}>
                        <td colSpan={9} style={{ padding: '12px 16px' }}>
                          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8, fontWeight: 600 }}>
                            Top 10 clés les plus volumineuses
                          </div>
                          <table style={{ width: '100%', fontSize: 12 }}>
                            <tbody>
                              {it.top_keys.map((tk, idx) => (
                                <tr key={tk.key + idx}>
                                  <td style={{ padding: '4px 8px', color: '#cbd5e1', fontFamily: 'monospace' }}>{tk.key}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right', color: '#fff' }}>{fmtKb(tk.bytes)} KB</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                                    {tk.compressed ? <CheckCircle2 size={14} style={{ color: '#84cc16' }} /> : <span style={{ color: '#64748b', fontSize: 10 }}>brut</span>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th = { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' };
const td = { padding: '12px', fontSize: 13, color: '#cbd5e1', verticalAlign: 'top' };
const iconBtn = { background: 'transparent', border: 0, color: '#94a3b8', cursor: 'pointer', padding: 6, margin: '0 2px' };

function KpiCard({ label, value, sub, color, icon }) {
  return (
    <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, padding: 16 }} data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
        {icon}{label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color, marginTop: 6 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
